// Pause introductions, the support side (founder go 2026-09-27: "yes on
// 1-4"). A member asks in writing to stop being introduced; support pauses
// them from the member page, and 2.4 members do it themselves from Settings.
// The server owns every effect (backend api/utils/introPause.js); this file
// only words what it reports, so the member page, the list badge and the
// tests all say the same thing.
//
// 🔴 Only ever on the member's OWN written request (decision 4, and the
// patent posture in the design: nothing pauses a member automatically).

// The reason chips of the 2.4 pause sheet, word for word, so the admin reads
// what the member tapped.
export const PAUSE_REASONS = [
  { value: "met_someone", label: "I met someone" },
  { value: "need_time", label: "I need some time" },
  { value: "something_else", label: "Something else" },
];

// The server refuses a note outside 1 to 300 characters (after trimming).
export const NOTE_MAX = 300;

export const reasonLabel = (reason) => {
  const hit = PAUSE_REASONS.find((r) => r.value === reason);
  return hit ? hit.label : null;
};

/** Paused means introPause.at is set; a missing field is not paused. */
export const isPaused = (user) => !!(user && user.introPause && user.introPause.at);

/**
 * "Sep 27, 3:12 PM ET". Always Eastern (house rule): the operator reads
 * Ottawa time whatever the browser's own zone is.
 */
export const formatEt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return (
    d.toLocaleString("en-US", {
      timeZone: "America/Toronto",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }) + " ET"
  );
};

/** What a pause was for: the member's chip, the support note, or both. */
export const pauseWhy = (introPause) => {
  if (!introPause) return "";
  const bits = [reasonLabel(introPause.reason), String(introPause.note || "").trim()].filter(Boolean);
  return bits.join("; ");
};

/**
 * The paused line, e.g. "Paused since Sep 27, 3:12 PM ET, by support (Asked
 * by email 09-27)". null when not paused. It sits after an "Introductions"
 * label (member page, account view) or on the "Paused" badge, so it never
 * repeats the word "Introductions" (review 2026-09-27).
 */
export const pauseLine = (introPause) => {
  if (!introPause || !introPause.at) return null;
  const by = introPause.by === "member" || introPause.by === "support" ? introPause.by : null;
  const why = pauseWhy(introPause);
  return (
    `Paused since ${formatEt(introPause.at)}` +
    (by ? `, by ${by}` : "") +
    (why ? ` (${why})` : "")
  );
};

/** The line when not paused: "On", or "On (resumed Sep 28, 10:02 AM ET)". */
export const onLine = (introPause) => {
  const resumedAt = introPause && introPause.resumedAt;
  const when = resumedAt ? formatEt(resumedAt) : "";
  return when ? `On (resumed ${when})` : "On";
};

/**
 * The "Introductions" row of the account view (user_lifecycle returns
 * introPause, null when never paused). A deleted account is dealt nothing
 * either way, so it only says whether they were paused when they left.
 * null = no row (a deleted account that was not paused, or a server that
 * does not send the field).
 */
export const lifecycleIntroLine = (introPause, deleted) => {
  if (introPause === undefined) return null;
  const paused = !!(introPause && introPause.at);
  if (deleted) return paused ? `Paused when they left (since ${formatEt(introPause.at)})` : null;
  return paused ? pauseLine(introPause) : onLine(introPause);
};

/**
 * Did the server say this call changed nothing? The admin endpoints answer
 * alreadyPaused (pause) and alreadyOn (resume); "already" is read too, in
 * case a server sends that. Only a literal true counts.
 */
export const wasAlready = (kind, data) => {
  if (!data) return false;
  if (data.already === true) return true;
  return kind === "paused" ? data.alreadyPaused === true : data.alreadyOn === true;
};

// Founder decision 2026-09-27 (the "pause then resume is a free End" review
// finding): resume stays instant, but a resume within this many hours of a
// pause that closed one of her cards counts that close as her own End (the
// End wait, the End's silent-partner rule). The server applies it.
export const RESUME_AS_END_HOURS = 24;

/** When the as-an-End window of this pause runs out ("" when not paused). */
export const resumeAsEndUntil = (introPause) => {
  if (!introPause || !introPause.at) return "";
  const at = new Date(introPause.at).getTime();
  if (Number.isNaN(at)) return "";
  return formatEt(at + RESUME_AS_END_HOURS * 60 * 60 * 1000);
};

/**
 * What a resume RIGHT NOW would do, for the resume form (review 2026-09-27,
 * item 7). The server works it out for a paused member and adds it to their
 * introPause block, the same answer the 2.4 resume sheet gets:
 *   resumeCountsAsEnd  true when a resume now counts the pause's close as
 *                      their End (the pause closed a card they held, and
 *                      the 24 hours have not run out)
 *   resumeFreeAt       from when it no longer would
 *   resumeWaitUntil    when their next introduction could come if they
 *                      resumed now; null when no wait would follow
 *
 * Returns { countsAsEnd, until, waitUntil, noWait }:
 *   countsAsEnd  true / false as the server says, null when it says nothing
 *                (an older server: the form then words the rule, not a
 *                verdict)
 *   until        "Sep 28, 3:12 PM ET" while the window is open, else ""
 *                (never shown once the 24 hours have passed)
 *   waitUntil    "Sep 28, 6:00 PM ET" when the server names a wait, else ""
 *   noWait       true only when the server said null (no wait would follow)
 */
export const resumeOutlook = (introPause, now = Date.now()) => {
  const out = { countsAsEnd: null, until: "", waitUntil: "", noWait: false };
  if (!introPause || !introPause.at) return out;
  const at = new Date(introPause.at).getTime();
  const windowEnd = Number.isNaN(at) ? NaN : at + RESUME_AS_END_HOURS * 60 * 60 * 1000;
  if (typeof introPause.resumeCountsAsEnd === "boolean") {
    out.countsAsEnd = introPause.resumeCountsAsEnd;
    if (out.countsAsEnd) {
      // A page left open past the window: the server's rule is "before
      // resumeFreeAt", so by now a resume would count nothing, and the
      // wait it worked out with the count is out of date too.
      const freeAt = new Date(introPause.resumeFreeAt || windowEnd).getTime();
      if (!Number.isNaN(freeAt) && now >= freeAt) return { countsAsEnd: false, until: "", waitUntil: "", noWait: false };
      out.until = formatEt(introPause.resumeFreeAt) || resumeAsEndUntil(introPause);
    }
  } else if (!Number.isNaN(windowEnd) && now < windowEnd) {
    // No verdict from the server: the window's end only while it is open,
    // as the rule's own time ("If this pause closed an introduction...").
    out.until = resumeAsEndUntil(introPause);
  }
  if (introPause.resumeWaitUntil) {
    // A wait already over by now says nothing; never show a past time.
    const w = new Date(introPause.resumeWaitUntil).getTime();
    if (!Number.isNaN(w) && w > now) out.waitUntil = formatEt(w);
  } else if (introPause.resumeWaitUntil === null) {
    out.noWait = true;
  }
  return out;
};

/**
 * The line under a resume result when the server counted the pause's close
 * as their End (POST /admin/users/:id/intro-resume, countedAsEnd = how many
 * cards). "" when it counted none.
 */
export const countedAsEndText = (countedAsEnd, who) => {
  const n = Number(countedAsEnd) || 0;
  if (n <= 0) return "";
  const name = who || "this member";
  const lead = `Back within ${RESUME_AS_END_HOURS} hours of the pause, so `;
  return n === 1
    ? `${lead}the introduction it closed counts as ${name}'s own End for the wait rules, and the ` +
        `other person may get a lapse (only if they had been shown it and done nothing with it).`
    : `${lead}the ${n} introductions it closed count as ${name}'s own Ends for the wait rules, and ` +
        `each other person may get a lapse (only if they had been shown theirs and done nothing with it).`;
};

/**
 * The wait line under a resume result (waitUntil, in Eastern). "" when the
 * server named no wait.
 */
export const waitUntilText = (waitUntil, who) => {
  const when = formatEt(waitUntil);
  return when ? `No new introduction for ${who || "this member"} before ${when}.` : "";
};

/**
 * The headline over what a REPEAT pause closed (review 2026-09-27, item 1).
 * A pause on a paused member writes no new pause and does not save the
 * note, but still closes any card they were left holding (backend
 * introPause.pauseMember, closed). Never "nothing was closed" unless the
 * server sent an empty list.
 */
export const repeatPauseClosedSummary = (closed) => {
  if (!Array.isArray(closed)) {
    return "The server did not say whether it closed anything. Check their introductions.";
  }
  if (!closed.length) return "Nothing new was closed.";
  const n = closed.length;
  return `Closed ${n} introduction${n === 1 ? "" : "s"} still open:`;
};

/** Returns an error string, or "" when the note is acceptable. */
export const validatePauseNote = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "A note is required: what the member asked, and where.";
  if (trimmed.length > NOTE_MAX) return `Keep the note to ${NOTE_MAX} characters or fewer.`;
  return "";
};

/**
 * The headline over the closed list. A response without the list never reads
 * as "nothing was closed": that would be a claim the server did not make.
 */
export const closedSummary = (closed) => {
  if (!Array.isArray(closed)) {
    return "The server did not say which introductions were closed. Check their introductions.";
  }
  if (!closed.length) return "There was no open introduction to close.";
  const n = closed.length;
  return `Closed ${n} open introduction${n === 1 ? "" : "s"}:`;
};

/** First word of a full name, or "" (the partner push uses the first name). */
export const firstNameOf = (fullName) => String(fullName || "").trim().split(/\s+/)[0] || "";

/**
 * One closed introduction from POST /admin/users/:id/intro-pause
 * ({ closed: [{ pairId, firstName, told }] }), in words.
 *
 * "Not told" carries no reason of ours (review 2026-09-27): told is false
 * when they had not answered or heard about it, but also when their account
 * is not active, the truth gate stopped the push, or the send failed, and the
 * server does not say which. A reason is shown only when the server sends one
 * (notToldReason, a sentence the server words).
 */
export const closedItemText = (item, pauserFirst) => {
  const name = (item && item.firstName) || "The other person";
  if (item && item.told) {
    return pauserFirst
      ? `${name}, told "${pauserFirst} is taking a break"`
      : `${name}, told they are taking a break`;
  }
  const why = item && typeof item.notToldReason === "string" ? item.notToldReason.trim() : "";
  return why ? `${name}, not told (${why})` : `${name}, not told`;
};
